/**
 * @file crm-deal-view.ts
 * @description Как показать сделку в кабинете магазина (phase19.31, зона
 * «Клиенты и сделки»; SC26–SC28, SC43, SC61): тон стадии, деньги, срок и
 * просрочка, источник словом.
 *
 * Чистые функции без React — одни и те же в реестре, на доске и в карточке:
 *  - тон пилюли стадии — по ВИДУ стадии (`currentStageKind`), цвет, который
 *    задал магазин, — только точкой (SC28). Иначе светлая стадия
 *    («Согласование» бежевым) не читалась на белом;
 *  - деньги — «7 500,00 ₽»; в таблицах у целых сумм допустимо «128 000 ₽»;
 *  - срок — из `slaDeadlineAt`; у закрытой сделки срока нет (просрочка у
 *    выигранной сделки — ложная тревога).
 */

import type { DealDto, StageKind } from "./api-crm";
import { getShopTimeZone } from "./shop-time";
import type { StatusTone } from "./shop/status-meta";
import { safeStageColor } from "./stage-color";

/**
 * Тон стадии по виду. Открытая — синий, выиграна — зелёный, не состоялась —
 * серый, на паузе — янтарный, отменена — красный (SC26: «отменено» красным).
 */
export const DEAL_STAGE_KIND_TONE: Record<StageKind, StatusTone> = {
  INITIAL: "progress",
  IN_PROGRESS: "progress",
  ON_HOLD: "attention",
  WON: "success",
  LOST: "neutral",
  CANCELLED: "error",
};

/** Виды стадий, на которых сделка закончена. */
const CLOSED_KINDS: readonly StageKind[] = ["WON", "LOST", "CANCELLED"];

export function isClosedStageKind(kind: StageKind): boolean {
  return CLOSED_KINDS.includes(kind);
}

// Проверка цвета стадии — общая с заказами и возвратами (`lib/stage-color.ts`);
// реэкспорт оставлен для прежних импортов сделок.
export { safeStageColor };

export interface DealStageView {
  label: string;
  tone: StatusTone;
  /** Цвет точки, который задал магазин; `null` — точка цвета тона. */
  dot: string | null;
}

export function dealStageView(
  deal: Pick<DealDto, "currentStageName" | "currentStageKind" | "currentStageColor">,
): DealStageView {
  return {
    label: deal.currentStageName,
    tone: DEAL_STAGE_KIND_TONE[deal.currentStageKind] ?? "neutral",
    dot: safeStageColor(deal.currentStageColor),
  };
}

/**
 * Деньги в рублях: «7 500,00 ₽». `whole` — у целой суммы без копеек
 * («128 000 ₽», для таблиц и списков), дробная остаётся с копейками.
 * Пусто — «—».
 */
const NBSP = String.fromCharCode(0xa0);

export function formatRub(value: string | number | null | undefined, mode: "kopecks" | "whole" = "kopecks"): string {
  if (value === null || value === undefined || value === "") return "—";
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return String(value);
  const digits = mode === "whole" && Number.isInteger(num) ? 0 : 2;
  const text = num.toLocaleString("ru-RU", { minimumFractionDigits: digits, maximumFractionDigits: digits });
  // Неразрывный пробел перед знаком рубля: «7 500,00 ₽» не разрывается переносом.
  return `${text}${NBSP}₽`;
}

/** Длительность коротко: «12 дн», «5 ч», «40 мин» (не «12д»). */
export function formatDurationShort(ms: number): string {
  const mins = Math.max(1, Math.floor(Math.abs(ms) / 60_000));
  if (mins < 60) return `${mins} мин`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} ч`;
  return `${Math.floor(hours / 24)} дн`;
}

/** «20.09» по часовому поясу магазина (SC31). */
export function formatDayMonth(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("ru-RU", { timeZone: getShopTimeZone(), day: "2-digit", month: "2-digit" });
}

/** Срок сделки: нет срока, ещё есть время или просрочено. */
export type DealDeadline =
  | { kind: "none" }
  | { kind: "due"; until: string; left: string }
  | { kind: "overdue"; until: string; overdue: string };

export function dealDeadline(
  deal: Pick<DealDto, "slaDeadlineAt" | "closedAt" | "currentStageKind">,
  now: number = Date.now(),
): DealDeadline {
  if (!deal.slaDeadlineAt || deal.closedAt || isClosedStageKind(deal.currentStageKind)) return { kind: "none" };
  const deadline = new Date(deal.slaDeadlineAt).getTime();
  if (Number.isNaN(deadline)) return { kind: "none" };
  const until = `до ${formatDayMonth(deal.slaDeadlineAt)}`;
  const diff = deadline - now;
  if (diff < 0) return { kind: "overdue", until, overdue: formatDurationShort(diff) };
  return { kind: "due", until, left: formatDurationShort(diff) };
}

/** Источники сделки словами; неизвестный — с заглавной буквы, как пришёл. */
const DEAL_SOURCE_LABEL: Record<string, string> = {
  site: "Сайт",
  telegram: "Telegram",
  vk: "ВКонтакте",
  avito: "Avito",
  email: "Почта",
  phone: "Телефон",
  instagram: "Instagram",
  whatsapp: "WhatsApp",
};

export function dealSourceLabel(source: string | null | undefined): string | null {
  const raw = (source ?? "").trim();
  if (!raw) return null;
  return DEAL_SOURCE_LABEL[raw.toLowerCase()] ?? raw[0]!.toUpperCase() + raw.slice(1);
}
